/**
 * Undoable constraint mutations (Faz 6.4): every constraint-set change is
 * ONE history step, following the entityOps pattern. The history closures
 * snapshot the whole serialized set — constraint records are small, so a
 * full-set swap keeps undo/redo exact without per-record bookkeeping.
 */

import { useAppStore } from '../../state/store';
import { useConstraintStore } from './constraintStore';
import { solveConstraintSet } from './solve';
import type { ConstraintPayload } from './types';

/** Apply a constraint-store mutation as ONE undoable step. */
export function undoableConstraints(
    mutate: () => void,
    label: string
): void {
    const before: ConstraintPayload = useConstraintStore.getState().serializePayload();
    mutate();
    const after: ConstraintPayload = useConstraintStore.getState().serializePayload();
    if (JSON.stringify(before) === JSON.stringify(after)) return;
    useAppStore.getState().pushHistory({
        type: 'cmd.constraint',
        timestamp: Date.now(),
        data: { label },
        undo: () => {
            useConstraintStore.getState().applyPayload(before);
            solveConstraintSet({ silent: true });
        },
        redo: () => {
            useConstraintStore.getState().applyPayload(after);
            solveConstraintSet({ silent: true });
        },
    });
}

/**
 * Collect the constraint ids that must die with the given entity ids
 * (referencing refs or bound dimensions) — used by entity deletion to keep
 * entity + constraint removal inside ONE undo step (OCS behavior).
 */
export function constraintIdsBoundTo(entityIds: string[]): string[] {
    const doomed = new Set(entityIds);
    const { set } = useConstraintStore.getState();
    return set.constraints
        .filter((c) =>
            c.refs.some((r) => doomed.has(r.entityId))
            || (set.dimensions[c.id] !== undefined && doomed.has(set.dimensions[c.id]))
        )
        .map((c) => c.id);
}
